// Shopify's Search & Discovery app has very limited typo tolerance outside primary fields
// (title/description) - artist names (stored as vendor) get almost no leeway there: a query
// like "Abby Loy" returns almost nothing for "Abie Loy Kemarre", even though "Loy" alone
// works fine, because non-primary-field tolerance only kicks in once the first four
// characters already match exactly.
//
// This resolves the customer's query against the full artist list (sourced from the
// `artists` metaobject - not capped like Search & Discovery's own filters, see
// snippets/artist-search-data.liquid) using Fuse.js, and rewrites the query to the matched
// artist's exact name before it reaches Shopify - only when confident. Unmatched queries pass
// through untouched so normal search is never broken.
//
// Deliberately does not touch predictive-search.js / search-form.js / main-search.js: it
// patches window.fetch (for the predictive dropdown's suggestion requests) and listens for
// `submit` on search forms in the capture phase, so it stays an additive "in-between" layer
// rather than a fork of Dawn's own search code.
(function () {
  const MATCH_THRESHOLD = 0.45;
  const MIN_QUERY_LENGTH = 3;

  let fuse;

  function getArtistNames() {
    const source = document.getElementById('ArtistSearchData');
    if (!source) return [];
    try {
      const names = JSON.parse(source.textContent);
      return Array.isArray(names) ? names.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function getFuse() {
    if (fuse !== undefined) return fuse;

    if (typeof Fuse === 'undefined') {
      fuse = null;
      return fuse;
    }

    const artistNames = getArtistNames();
    fuse = artistNames.length
      ? new Fuse(artistNames, {
          includeScore: true,
          ignoreLocation: true,
          threshold: MATCH_THRESHOLD,
          distance: 100,
        })
      : null;

    return fuse;
  }

  function resolveQuery(query) {
    const trimmed = (query || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return trimmed;

    const instance = getFuse();
    if (!instance) return trimmed;

    const results = instance.search(trimmed);
    if (!results.length) return trimmed;

    const bestMatch = results[0];
    if (typeof bestMatch.score !== 'number' || bestMatch.score > MATCH_THRESHOLD) return trimmed;

    // Already an exact (or exact-enough) match to itself - nothing to correct.
    if (bestMatch.item.toLowerCase() === trimmed.toLowerCase()) return trimmed;

    return bestMatch.item;
  }

  window.ArtistSearchTolerance = { resolveQuery };

  function predictiveSearchPathname() {
    if (!window.routes || !window.routes.predictive_search_url) return null;
    return new URL(window.routes.predictive_search_url, window.location.origin).pathname;
  }

  // Shopify's response echoes back whatever term was actually searched (the corrected one) in
  // exactly two places (see sections/predictive-search.liquid): the "Search for X" button text
  // and the live-region "no results for X" message. predictive-search.js's own
  // updateSearchForTerm() assumes the button text still literally contains whatever the
  // customer previously typed, so it can find-and-replace it as they keep typing - since the
  // corrected term replaces it instead, that assumption breaks and Dawn's own code throws
  // (regex .match() returns null, then .length crashes).
  //
  // This has to be a surgical DOM edit of just those two elements, not a blind string replace
  // across the whole response - the corrected term is, by construction, the same string as the
  // matched products' actual vendor name, so a global replace would also corrupt every vendor
  // caption in the results back to the typed term.
  function rewriteTermsInResponse(response, correctedTerm, originalTerm) {
    return response.text().then((text) => {
      if (correctedTerm === originalTerm) {
        return new Response(text, { status: response.status, statusText: response.statusText, headers: response.headers });
      }

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const targets = [
        doc.querySelector('[data-predictive-search-search-for-text]'),
        doc.querySelector('[data-predictive-search-live-region-count-value]'),
      ];

      targets.forEach((element) => {
        if (element) element.textContent = element.textContent.split(correctedTerm).join(originalTerm);
      });

      return new Response(doc.documentElement.outerHTML, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    });
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Defensive safety net, independent of rewriteTermsInResponse above: predictive-search.js's
  // own updateSearchForTerm() assumes the current "Search for X" button text always literally
  // contains the previous search term, and crashes (regex .match() returns null, then .length
  // throws) whenever it doesn't. rewriteTermsInResponse() covers the common case, but Dawn's
  // own predictive-search.js never cancels or sequences overlapping requests between keystrokes
  // (it reuses one AbortController that's only reset on close/blur) - since a corrected query
  // takes slightly longer to resolve here (extra parsing to patch the response), that makes
  // out-of-order responses more likely than in stock Dawn, which can still leave a stale button
  // on screen that doesn't contain the expected term. Patched to safely no-op instead of
  // crashing whenever that assumption doesn't hold, regardless of the reason.
  customElements.whenDefined('predictive-search').then(() => {
    const PredictiveSearch = customElements.get('predictive-search');
    if (typeof PredictiveSearch.prototype.updateSearchForTerm !== 'function') return;

    PredictiveSearch.prototype.updateSearchForTerm = function (previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      const currentButtonText = searchForTextElement?.innerText;
      if (!currentButtonText || !previousTerm) return;

      let matches;
      try {
        matches = currentButtonText.match(new RegExp(escapeRegExp(previousTerm), 'g'));
      } catch (error) {
        return;
      }
      // Zero matches (previously crashed here) or an ambiguous multi-match - leave the button
      // text alone rather than guess.
      if (!matches || matches.length > 1) return;

      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    };
  });

  // Predictive search dropdown: rewrite the query used for the AJAX suggestion request only -
  // never what the customer sees typed in the box, so it doesn't fight their typing.
  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    try {
      const requestUrl = typeof input === 'string' || input instanceof URL ? input : input.url;
      const url = new URL(requestUrl, window.location.origin);
      const predictivePathname = predictiveSearchPathname();

      if (predictivePathname && url.pathname === predictivePathname) {
        const q = url.searchParams.get('q');
        if (q) {
          const resolved = resolveQuery(q);
          if (resolved !== q) {
            url.searchParams.set('q', resolved);
            return originalFetch(url.toString(), init).then((response) => rewriteTermsInResponse(response, resolved, q));
          }
        }
      }
    } catch (error) {
      // Fall through and issue the request unmodified.
    }
    return originalFetch(input, init);
  };

  // Full search results page: rewrite `q` before the form actually submits. Capture phase so
  // this runs before predictive-search.js's own submit handler reads the input's value.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;

      const input = form.querySelector('input[name="q"]');
      if (!input || !input.value) return;

      const resolved = resolveQuery(input.value);
      if (resolved !== input.value) input.value = resolved;
    },
    true
  );
})();
